import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const LOGO_URL = '/RemaH.png';

const validatePassword = (password) => {
  if (password.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères';
  if (!/[A-Z]/.test(password)) return 'Le mot de passe doit contenir au moins une majuscule';
  if (!/[a-z]/.test(password)) return 'Le mot de passe doit contenir au moins une minuscule';
  if (!/[0-9]/.test(password)) return 'Le mot de passe doit contenir au moins un chiffre';
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password))
    return 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)';
  return null;
};

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score++;
  if (score <= 2) return { score, label: 'Faible', color: 'bg-red-500' };
  if (score <= 4) return { score, label: 'Moyen', color: 'bg-yellow-500' };
  return { score, label: 'Fort', color: 'bg-green-500' };
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [oobCode, setOobCode] = useState(null);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const code = searchParams.get('oobCode');
    if (!code) {
      toast.error('Lien invalide');
      navigate('/');
      return;
    }

    verifyPasswordResetCode(auth, code)
      .then((resolvedEmail) => {
        setOobCode(code);
        setEmail(resolvedEmail);
      })
      .catch(() => {
        toast.error('Lien expiré ou invalide. Faites une nouvelle demande.');
        navigate('/forgot-password');
      });
  }, [searchParams, navigate]);

  const strength = getPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validatePassword(newPassword);
    if (error) {
      toast.error(error);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setDone(true);
      toast.success('Mot de passe réinitialisé avec succès !');
    } catch (err) {
      if (err.code === 'auth/expired-action-code') {
        toast.error('Lien expiré. Faites une nouvelle demande.');
        navigate('/forgot-password');
      } else if (err.code === 'auth/invalid-action-code') {
        toast.error('Lien invalide ou déjà utilisé.');
        navigate('/forgot-password');
      } else {
        toast.error('Erreur lors de la réinitialisation. Réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  // En attente de vérification du code
  if (!oobCode && !done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-purple-950 flex items-center justify-center">
        <p className="text-white/60 text-sm animate-pulse">Vérification du lien...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-purple-950 relative overflow-hidden flex items-center justify-center px-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Remadia"
            className="w-56 h-56 mx-auto mb-4 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
        </div>

        <Card className="p-8 bg-white/10 backdrop-blur-xl border-2 border-white/20 shadow-2xl rounded-3xl">
          {done ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Mot de passe mis à jour !</h2>
              <p className="text-white/60 text-sm mb-6">
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg py-6 text-lg rounded-xl transition-all"
              >
                Se connecter
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <KeyRound className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Nouveau mot de passe</h1>
                {email && (
                  <p className="text-white/50 text-sm">
                    Compte : <span className="text-cyan-400">{email}</span>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Nouveau mot de passe */}
                <div>
                  <Label htmlFor="new-password" className="text-white mb-2 block">
                    Nouveau mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Indicateur de force */}
                  {newPassword && (
                    <div className="mt-2 space-y-1">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                              i <= strength.score ? strength.color : 'bg-white/20'
                            }`}
                          />
                        ))}
                      </div>
                      {strength.label && (
                        <p className="text-xs text-white/50">
                          Force : <span className="text-white/80">{strength.label}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Confirmation */}
                <div>
                  <Label htmlFor="confirm-password" className="text-white mb-2 block">
                    Confirmer le mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                      tabIndex={-1}
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-400 mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                {/* Règles du mot de passe */}
                <ul className="text-xs text-white/40 space-y-0.5 pl-1">
                  {[
                    [/.{8,}/, '8 caractères minimum'],
                    [/[A-Z]/, 'Une majuscule'],
                    [/[a-z]/, 'Une minuscule'],
                    [/[0-9]/, 'Un chiffre'],
                    [/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/, 'Un caractère spécial'],
                  ].map(([regex, label]) => (
                    <li
                      key={label}
                      className={regex.test(newPassword) ? 'text-green-400' : ''}
                    >
                      {regex.test(newPassword) ? '✓' : '○'} {label}
                    </li>
                  ))}
                </ul>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg hover:shadow-cyan-500/50 py-6 text-lg rounded-xl transition-all"
                  disabled={loading}
                >
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            </>
          )}

          <button
            onClick={() => navigate('/')}
            className="mt-6 flex items-center justify-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors w-full"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </button>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
